import React, { Fragment, useEffect, useState } from 'react'
import { Box, Link, styled, Typography } from '@mui/material'
import { isNilOrEmpty, useIsSmallScreen, useQuery } from 'packages/core'
import { useTranslation, Trans } from 'react-i18next'
import {
    ApplicationsList,
    DifferentiationValueSelector,
    ItemDetails,
    SuggestedRoles,
    TCodesList,
} from 'components'
import {
    useAccessRequestPolicy,
    useBusinessRoleApplications,
    useBusinessRoleDifferentiationValues,
    useBusinessRoleSuggestedApplicationRoles,
    useBusinessRoleSuggestedManagementRoles,
    useCheckBusinessRoleAccess,
    useTargetPerson,
    useTCodeGrantedByBusinessRole,
} from 'hooks'
import { useResourceTypeContext } from 'resourceTypeContext'
import { DifferentiationValueIcon, Icon } from 'packages/eid-icons'
import { genericHashLink } from 'utils'
import useSubcomponents from 'useSubcomponents'
import cartHelpers from 'containers/Cart/cartHelpers'
import { MoreInformationSection } from './MoreInformationSection'
import { OverviewSection } from './OverviewSection'
import { ApplicationRolesGranted } from './ApplicationRolesGranted'
import { ManagementRolesGranted } from './ManagementRolesGranted'
import { LocalSensitiveFunctions } from './LocalSensitiveFunctions'
import { Approvers } from './Approvers'
import { AddToCart } from 'components/AddToCart'
import { ResourceTypesNamespace, useRegistry } from 'core'
import appConfig from 'config'
import ActivateNowButton from '../ActivateNowButton'
import { Skeleton } from '@mui/material'
import useConfiguration from 'useConfiguration'
import TextWithIcon from 'components/common/TextWithIcon'

const HashLink = genericHashLink(Link)

const businessRolePrimaryTabExplain = {
    overview:
        'Overview tab: business role name, domain, classification, description, access policy when loaded, and any published instructions.',
    additionalInformation:
        'More information tab: owners, responsible party, and global sensitive functions with search and pagination.',
}

const businessRoleSubTabExplain = {
    applicationRolesGranted:
        'Granted application roles for this business role and the selected differentiation option.',
    managementRolesGranted:
        'Granted management roles linked to this business role for the selected differentiation.',
    localSensitiveFunctions:
        'Local sensitive functions available under this business role for the selected differentiation (search and paging).',
    approvers:
        'People or groups who may approve access requests for this business role and differentiation.',
    tCodes: 'T-Codes linked to this business role for the selected differentiation: search the grid and use pagination.',
    applications:
        'Applications associated with this business role for the selected differentiation: search and browse results.',
}

const PaddedDiv = styled(Box)({
    padding: '0 31px',
})

const PromptContainer = styled('div')({
    padding: '48px 31px 20px 31px',
    minHeight: '232px',
    display: 'flex',
})

const BusinessRoleDetails = ({ businessRole, toggleDrawer }) => {
    const { t } = useTranslation()
    const registry = useRegistry()
    const { getResourceType } = useConfiguration()
    const query = useQuery()

    const currentResourceType = getResourceType('BusinessRoles')

    const businessRoleType = registry.get(
        ResourceTypesNamespace,
        'BusinessRoles',
    )

    const isSmallScreen = useIsSmallScreen()

    const {
        hasAccessToTCodesGrid,
        hasAccessToLocalSensitiveFunctionsGrid,
        canSeeManagementRolesGranted,
        canSeeApplicationRolesGranted,
        canSeeApplications,
        canSeeManagementRolesSuggestedGrid,
    } = useSubcomponents()

    const [targetPerson] = useTargetPerson()
    const [{ referencePerson, showPreApproved, showSuggested, tCode }] =
        useResourceTypeContext()

    const {
        data: differentiationValues,
        isInitialLoading: isInitialLoadingDifferentiationValues,
    } = useBusinessRoleDifferentiationValues(
        businessRole.id,
        targetPerson.id,
        referencePerson?.id,
        showPreApproved,
        showSuggested,
    )
    const selectedRoleId = query.get('selected')

    const [activeTab, setActiveTab] = useState('overview')
    const [subActiveTab, setSubActiveTab] = useState('applicationRolesGranted')

    const [differentiationValue, setDifferentiationValue] = useState(null)

    const { data: assignmentStatus, isInitialLoading: isCheckingAccess } =
        useCheckBusinessRoleAccess(
            businessRole.id,
            differentiationValue?.id,
            targetPerson.id,
        )

    const { data: tCodesData, isInitialLoading: isInitialLoadingTCodes } =
        useTCodeGrantedByBusinessRole(
            selectedRoleId,
            differentiationValue?.id,
            tCode?.id,
        )

    useEffect(() => {
        if (differentiationValues && differentiationValues.length === 1) {
            setDifferentiationValue(differentiationValues[0])
        }
    }, [differentiationValues, setDifferentiationValue])

    useEffect(() => {}, [differentiationValue])

    const preAdd = (baseObj) => {
        const itemToAdd = cartHelpers.businessRoleToCartItem({
            targetPerson,
            assignmentType: 'Add',
            businessRole,
            differentiationValue,
            assignmentStatus,
            ...baseObj,
        })
        if (!itemToAdd) return undefined

        return itemToAdd
    }

    const [suggestedRolesCount, setSuggestedRolesTotalCount] = useState(null)

    const policy = useAccessRequestPolicy(
        '',
        businessRole.id,
        differentiationValue?.id,
    )

    const scrollToSuggestedApplicationRolesLink = (
        <HashLink
            smooth
            component="button"
            underline="always"
            to={`${appConfig.APP_SUBPATH}/BusinessRoles?selected=${businessRole.id}#SuggestedApplicationRoles`}
            scroll={(el) => el.scrollIntoView()}
        >
            {t('BusinessRoles_SeeSuggestedApplicationRoles', {
                count: suggestedRolesCount,
            })}
        </HashLink>
    )

    const selectDifferentiationValueLabel = (
        <Typography
            style={{
                fontSize: '14px',
                lineHeight: 1.43,
                fontWeight: 600,
                textTransform: 'uppercase',
            }}
        >
            {t('BusinessRoles_SelectDifferentiationValue')}
        </Typography>
    )

    const differentiationValueSelector = (
        <DifferentiationValueSelector
            loading={isInitialLoadingDifferentiationValues}
            onChange={(val) => setDifferentiationValue(val)}
            data={differentiationValues}
            value={differentiationValue?.id}
        />
    )

    const getPreValidationMessage = () => {
        if (!Boolean(differentiationValue)) {
            return t('BusinessRoles_SelectDifferentiationValue')
        }
        if (assignmentStatus && assignmentStatus.isAssigned) {
            return t('ResourceAlreadyAssigned')
        }
        return ''
    }

    const TCodeInfoComponent = (
        <Box display="flex" alignItems="flex-start">
            <Box
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                }}
            >
                <Icon
                    name="InfoNew"
                    style={{
                        minHeight: '1.6rem',
                        minWidth: '1.4rem',
                    }}
                />

                <Box
                    style={{
                        display: 'block',
                        margin: '0 0 0',
                    }}
                >
                    <p
                        style={{
                            display: 'inline',
                            marginLeft: '0.5rem',
                        }}
                    >
                        {tCodesData?.length > 0 ? (
                            t('CombinationWillGrantTCode')
                        ) : (
                            <Trans
                                i18nKey="Common_CombinationWillNotGrantTCode"
                                components={{
                                    span: <span style={{ color: 'red' }} />,
                                }}
                            />
                        )}
                        : <strong>{tCode?.friendlyName}</strong>
                    </p>
                </Box>
            </Box>
        </Box>
    )

    return (
        <>
            <Box>
                <ItemDetails.Tabs
                    variant="standard"
                    value={activeTab}
                    onChange={(_, value) => setActiveTab(value)}
                >
                    <ItemDetails.Tabs.Tab
                        value="overview"
                        label={t('Common_Overview')}
                        data-ai-explain={businessRolePrimaryTabExplain.overview}
                    />
                    <ItemDetails.Tabs.Tab
                        value="additionalInformation"
                        label={t('Common_MoreInformation')}
                        data-ai-explain={
                            businessRolePrimaryTabExplain.additionalInformation
                        }
                    />
                </ItemDetails.Tabs>
            </Box>

            {activeTab === 'overview' && (
                <OverviewSection
                    businessRole={businessRole}
                    policy={policy}
                    loading={isCheckingAccess}
                />
            )}

            {activeTab === 'additionalInformation' && (
                <MoreInformationSection businessRole={businessRole} />
            )}

            {!isSmallScreen && (
                <>
                    <Box data-ai-explain="Differentiation selector (desktop): choose the business role option that determines which access package, checks, and lower tabs apply before requesting access.">
                        <PromptContainer>
                            <Box
                                width="172px"
                                display="flex"
                                flexDirection="column"
                                zIndex={1}
                            >
                                {selectDifferentiationValueLabel}

                                <Box
                                    marginTop="24px"
                                    width="100%"
                                    display="flex"
                                    justifyContent="center"
                                >
                                    <DifferentiationValueIcon />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: '3.2rem' }}>
                                <Box width="334px" paddingLeft="16px">
                                    {differentiationValueSelector}
                                </Box>
                                {differentiationValue?.instructions && (
                                    <Box>
                                        <TextWithIcon
                                            iconName={'InfoNew'}
                                            message={
                                                differentiationValue?.instructions
                                            }
                                        />
                                    </Box>
                                )}
                            </Box>
                        </PromptContainer>
                    </Box>
                    {tCode && differentiationValue?.id && (
                        <>
                            {isInitialLoadingTCodes ? (
                                <Box padding={'24px'}>
                                    <Skeleton height={40} />
                                </Box>
                            ) : (
                                <Box
                                    padding="0 31px"
                                    data-ai-explain="T-Code eligibility message: whether the selected differentiation together with your catalog T-Code filter would grant the highlighted T-Code."
                                >
                                    {TCodeInfoComponent}
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}

            {isSmallScreen && (
                <PaddedDiv>
                    <Box
                        paddingY="24px"
                        display="flex"
                        flexDirection="column"
                        position="relative"
                        data-ai-explain="Differentiation selector (mobile): pick the business role option before add to cart; the same choice drives access checks and the lower tabbed sections."
                    >
                        <Box position="absolute" top="16px" right="0">
                            <DifferentiationValueIcon
                                width="107px"
                                height="100px"
                            />
                        </Box>

                        <Box width="172px" paddingY="4px" zIndex={1}>
                            {selectDifferentiationValueLabel}
                        </Box>

                        <Box paddingY="4px">{differentiationValueSelector}</Box>
                    </Box>
                </PaddedDiv>
            )}
            {isCheckingAccess ? (
                <Box padding={'24px'}>
                    <Skeleton height={40} />
                </Box>
            ) : assignmentStatus?.canActivateNow ? (
                <ActivateNowButton
                    policyId={policy?.id}
                    resource={businessRole}
                    resourceType={currentResourceType}
                    assignment={assignmentStatus}
                    forDetailsDrawer={true}
                    width="300px"
                    resourceId={
                        assignmentStatus?.resourceAssignment
                            ?.resourceAssignmentId
                    }
                />
            ) : (
                <Box data-ai-explain="Add this business role (with the selected differentiation when required) to the cart to start an access request.">
                    <AddToCart
                        resourceType={businessRoleType}
                        resource={businessRole}
                        secondary={differentiationValue}
                        preValidate={getPreValidationMessage}
                        preAdd={preAdd}
                        postAdd={toggleDrawer}
                    />
                </Box>
            )}

            {differentiationValue && suggestedRolesCount > 0 && (
                <Box
                    paddingX="31px"
                    paddingY="16px"
                    textAlign="right"
                    data-ai-explain="Shortcut link to scroll the page to suggested application roles for this business role and differentiation."
                >
                    {scrollToSuggestedApplicationRolesLink}
                </Box>
            )}

            {differentiationValue && (
                <Fragment>
                    <Box>
                        <ItemDetails.Tabs
                            variant={'scrollable'}
                            scrollButtons={'on'}
                            value={subActiveTab}
                            onChange={(_, value) => setSubActiveTab(value)}
                        >
                            {canSeeApplicationRolesGranted && (
                                <ItemDetails.Tabs.Tab
                                    value="applicationRolesGranted"
                                    label={t(
                                        'BusinessRoles_ApplicationRolesGranted',
                                    )}
                                    data-protectedsubcomponent={
                                        canSeeApplicationRolesGranted
                                    }
                                    data-ai-explain={
                                        businessRoleSubTabExplain.applicationRolesGranted
                                    }
                                />
                            )}

                            {canSeeManagementRolesGranted && (
                                <ItemDetails.Tabs.Tab
                                    value="managementRolesGranted"
                                    label={t('Common_ManagementRolesGranted')}
                                    data-protectedsubcomponent={
                                        canSeeManagementRolesGranted
                                    }
                                    data-ai-explain={
                                        businessRoleSubTabExplain.managementRolesGranted
                                    }
                                />
                            )}

                            {hasAccessToLocalSensitiveFunctionsGrid && (
                                <ItemDetails.Tabs.Tab
                                    value="localSensitiveFunctions"
                                    label={t('Common_LocalFunctionalAccess')}
                                    data-protectedsubcomponent={
                                        hasAccessToLocalSensitiveFunctionsGrid
                                    }
                                    data-ai-explain={
                                        businessRoleSubTabExplain.localSensitiveFunctions
                                    }
                                />
                            )}
                            <ItemDetails.Tabs.Tab
                                value="approvers"
                                label={t('Common_Approvers')}
                                disabled={isNilOrEmpty(differentiationValue)}
                                data-ai-explain={
                                    businessRoleSubTabExplain.approvers
                                }
                            />
                            {hasAccessToTCodesGrid && (
                                <ItemDetails.Tabs.Tab
                                    value="tCodes"
                                    label={t('Common_TCodes')}
                                    data-protectedsubcomponent={
                                        hasAccessToTCodesGrid
                                    }
                                    data-ai-explain={
                                        businessRoleSubTabExplain.tCodes
                                    }
                                />
                            )}
                            {canSeeApplications && (
                                <ItemDetails.Tabs.Tab
                                    value="applications"
                                    label={t('Common_Applications')}
                                    data-protectedsubcomponent={
                                        canSeeApplications
                                    }
                                    data-ai-explain={
                                        businessRoleSubTabExplain.applications
                                    }
                                />
                            )}
                        </ItemDetails.Tabs>
                    </Box>

                    <Box overflow="auto">
                        {subActiveTab === 'applicationRolesGranted' && (
                            <ApplicationRolesGranted
                                businessRole={businessRole}
                                differentiationValue={differentiationValue}
                            />
                        )}

                        {subActiveTab === 'managementRolesGranted' && (
                            <ManagementRolesGranted
                                businessRole={businessRole}
                                differentiationValue={differentiationValue}
                            />
                        )}

                        {subActiveTab === 'localSensitiveFunctions' && (
                            <LocalSensitiveFunctions
                                businessRole={businessRole}
                                differentiationValue={differentiationValue}
                            />
                        )}
                        {subActiveTab === 'approvers' && (
                            <Approvers
                                businessRole={businessRole}
                                differentiationValue={differentiationValue}
                            />
                        )}
                        {subActiveTab === 'tCodes' && (
                            <Box data-ai-explain="T-Codes for this business role and differentiation: filter with search and use pagination in the grid below.">
                                <TCodesList
                                    resourceId={businessRole.id}
                                    differentiationValueId={
                                        differentiationValue.id
                                    }
                                />
                            </Box>
                        )}

                        {subActiveTab === 'applications' && (
                            <Box data-ai-explain="Applications for this business role and differentiation: search the list and paginate when many results exist.">
                                <ApplicationsList
                                    resource={businessRole}
                                    useDataHook={useBusinessRoleApplications}
                                    locationId={differentiationValue?.id}
                                />
                            </Box>
                        )}
                    </Box>
                    <Box
                        overflow="auto"
                        width="100%"
                        id="SuggestedApplicationRoles"
                        data-ai-explain="Suggested application roles: optional related roles you may request for the same business role and differentiation."
                    >
                        <SuggestedRoles
                            title={t('BusinessRoles_SuggestedApplicationRoles')}
                            toggleDrawer={toggleDrawer}
                            resource={businessRole}
                            differentiationValue={differentiationValue}
                            targetPerson={targetPerson}
                            useDataHook={
                                useBusinessRoleSuggestedApplicationRoles
                            }
                            onDataLoaded={(data) =>
                                setSuggestedRolesTotalCount(data.totalCount)
                            }
                        />
                    </Box>
                    {canSeeManagementRolesSuggestedGrid && (
                        <Box
                            overflow="auto"
                            width="100%"
                            id="SuggestedManagementRoles"
                            data-protectedsubcomponent={
                                canSeeManagementRolesSuggestedGrid
                            }
                            data-ai-explain="Suggested management roles: optional related management roles for this business role and differentiation."
                        >
                            <SuggestedRoles
                                title={t('Common_SuggestedManagementRoles')}
                                toggleDrawer={toggleDrawer}
                                resource={businessRole}
                                differentiationValue={differentiationValue}
                                targetPerson={targetPerson}
                                useDataHook={
                                    useBusinessRoleSuggestedManagementRoles
                                }
                            />
                        </Box>
                    )}
                </Fragment>
            )}
        </>
    )
}

export default BusinessRoleDetails
